import React, { useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@material-ui/core/Typography';
import {
  ActionButtonHandle,
  FormPanel,
  RemoteTableField,
  ScrollableSource,
  TableFieldHandle,
  TableFieldToolbarProps,
} from '@eas/common-web';
import { useAutoCollapse } from '@components/form/misc/auto-collapse-hook';
import { FormTableCount } from '@components/form/table-field/form-table-count';
import { EsFile, Proposer } from '@models';
import { useProposerColumns } from './proposer-columns';
import { FormFields } from './proposer-dialog';
import { ProposerToolbar } from './proposer-toolbar';

export interface ProposerFieldProps {
  disabled: boolean;
  esFile: EsFile | undefined;
  permissions?: ProposerFieldPermissions;
  source?: ScrollableSource<Proposer>;
}

export interface ProposerFieldPermissions {
  contact: boolean;
  type: boolean;
  distinction: boolean;
}

export function ProposerField({
  disabled,
  esFile,
  permissions,
  source,
}: ProposerFieldProps) {
  const { panelRef } = useAutoCollapse(source?.items ?? []);

  const columns = useProposerColumns();

  const tableRef = useRef<TableFieldHandle>(null);
  const editRef = useRef<ActionButtonHandle>(null);

  const effectivePermissions = permissions ?? {
    contact: !disabled,
    type: !disabled,
    distinction: !disabled,
  };

  const canEditAnyProposerField =
    effectivePermissions.contact ||
    effectivePermissions.type ||
    effectivePermissions.distinction;

  const tableDisabled = permissions ? false : disabled;

  const ToolbarComponent = useMemo(
    () =>
      function Toolbar(props: TableFieldToolbarProps) {
        return (
          <ProposerToolbar
            {...props}
            ref={editRef}
            disabled={disabled}
            esFile={esFile}
            permissions={effectivePermissions}
            hasGranularPermissions={permissions !== undefined}
            canEditAnyField={canEditAnyProposerField}
          />
        );
      },
    [canEditAnyProposerField, disabled, effectivePermissions, esFile]
  );

  if (!source) {
    return <> Missing PROPOSER_SOURCE </>;
  }

  return (
    <FormPanel
      label={
        <FormattedMessage
          id="ES__PROPOSERSS__PANEL_TITLE"
          defaultMessage="Navrhovatelé"
        />
      }
      expandable={true}
      ref={panelRef}
      summary={<FormTableCount source={source} />}
    >
      <RemoteTableField
        ref={tableRef}
        source={source}
        // disable sorting localy - #USCR-2415, needed for e.g. address field
        columns={columns.map((c) => ({ ...c, sortkey: undefined }))}
        onRowClick={(_, index) => {
          if (canEditAnyProposerField) {
            tableRef.current?.setSelectedIndex(index);
            editRef.current?.executeAction();
          } else if (!tableDisabled) {
            tableRef.current?.setSelectedIndex(index);
          }
        }}
        maxRows={10}
        ToolbarComponent={ToolbarComponent}
        disabled={tableDisabled}
        showDetailBtnCond={() => true}
        FormFieldsComponent={() => (
          <div style={{ width: 700 }}>
            <Typography variant="body1" style={{ marginBottom: 8 }}>
              <FormattedMessage
                id="ES__PROPOSERS__DESCRIPTION"
                defaultMessage="Navrhovatel:"
              />
            </Typography>
            <FormFields esFile={esFile} permissions={effectivePermissions} />
          </div>
        )}
      />
    </FormPanel>
  );
}
